package com.siriusxm.pia.views.accesscontrols

import kotlinx.serialization.json.JsonElement
import kotlinx.serialization.json.JsonObject
import kotlinx.serialization.json.JsonPrimitive
import kotlinx.serialization.json.booleanOrNull
import kotlinx.serialization.json.intOrNull

/**
 * The three access-control attributes for a single variant. If you add, rename, or remove a
 * field here, also update [toAccessControlState] (parses this shape from JSON) and
 * [ACCESS_CONTROL_FIELDS] (enumerates it for display) — nothing enforces these three staying in
 * sync, so a mismatch compiles cleanly but the new field silently never renders.
 */
data class AccessControlState(
    val visible: Boolean,
    val discoverable: Boolean,
    val recommendable: Boolean,
)

data class AccessControlVariant(
    val key: String,
    val label: String,
    val state: AccessControlState,
    val implied: Boolean = false,
) {
    val displayLabel: String
        get() = if (implied) "Not set - Implied $label" else label
}

/**
 * A single access-control attribute: its display label and how to read it off a state.
 * Internal — [AccessControlsBadgeView][com.siriusxm.pia.views.accesscontrols.AccessControlsBadgeView]
 * is the reusable surface for other apps (EPG, sports); this and the other classification
 * helpers below are that composable's own implementation detail, not public API in their own
 * right, until an actual external consumer needs to build custom UI on the same classification.
 */
internal data class AccessControlField(val label: String, val select: (AccessControlState) -> Boolean)

/**
 * The three access-control attributes, in display order. Shared by every view that renders a
 * per-variant or cross-variant badge group, so both stay in sync with each other; adding a
 * fourth control still separately requires a new [AccessControlState] field, a
 * [toAccessControlState] parse rule, and any view-side layout adjustment — see the note on
 * [AccessControlState] for the three places that must move together.
 */
internal val ACCESS_CONTROL_FIELDS = listOf(
    AccessControlField("Visible") { it.visible },
    AccessControlField("Discoverable") { it.discoverable },
    AccessControlField("Recommendable") { it.recommendable },
)

/**
 * How a single control (visible/discoverable/recommendable) agrees across a set of variants:
 * uniformly on, uniformly off, or disagreeing between variants (e.g. default disables
 * recommendable but auto360l enables it).
 */
internal enum class ControlAgreement { ALL_ENABLED, ALL_DISABLED, MIXED }

/**
 * The single classification rule behind [ControlAgreement]: uniformly true, uniformly false, or
 * a mix. Used both for a single boolean (wrapped in a one-element list, always resolving to
 * ALL_ENABLED/ALL_DISABLED and never MIXED) and for a whole variant list via [agreementFor], so
 * there's exactly one place that decides what "agrees" means.
 *
 * @throws IllegalArgumentException if [this] is empty — `all{}`/`none{}` are both vacuously true
 *   on an empty list, which would otherwise silently return ALL_ENABLED for "no data" rather than
 *   signaling the caller's mistake.
 */
internal fun List<Boolean>.toControlAgreement(): ControlAgreement {
    require(isNotEmpty()) { "toControlAgreement requires at least one value, got an empty list" }
    return when {
        all { it } -> ControlAgreement.ALL_ENABLED
        none { it } -> ControlAgreement.ALL_DISABLED
        else -> ControlAgreement.MIXED
    }
}

/**
 * Determines whether [select] (a single control accessor) agrees across every variant in this
 * list. Used by collapsed/summary views so a control that's disabled for one variant but
 * enabled for another reads as a "check the details" signal rather than a flat on/off.
 *
 * [AccessControlsBadgeView][com.siriusxm.pia.views.accesscontrols.AccessControlsBadgeView] never
 * calls this on an empty list — it returns early on an empty variants list before calling in —
 * so the [toControlAgreement] precondition is never hit in practice.
 */
internal fun List<AccessControlVariant>.agreementFor(select: (AccessControlState) -> Boolean): ControlAgreement =
    map { select(it.state) }.toControlAgreement()

/**
 * Each field in [ACCESS_CONTROL_FIELDS] paired with its [ControlAgreement] across this list of
 * variants. Returned as explicit pairs (not a bare list positionally matched against
 * [ACCESS_CONTROL_FIELDS] elsewhere) so callers can't desync the two by reordering or filtering
 * one but not the other.
 */
internal fun List<AccessControlVariant>.agreementsByField(): List<Pair<AccessControlField, ControlAgreement>> =
    ACCESS_CONTROL_FIELDS.map { field -> field to agreementFor(field.select) }

private val impliedDefaultState = AccessControlState(true, true, true)

fun impliedDefaultAccessControlVariant(): AccessControlVariant = AccessControlVariant(
    key = "default",
    label = "Default",
    state = impliedDefaultState,
    implied = true,
)

fun JsonObject?.toAccessControlVariants(includeImpliedDefault: Boolean = false): List<AccessControlVariant> {
    this ?: return if (includeImpliedDefault) listOf(impliedDefaultAccessControlVariant()) else emptyList()

    val variants = entries.mapNotNull { (key, value) ->
        if (value is JsonObject) {
            value.toAccessControlState()?.let { state ->
                AccessControlVariant(key, key.toVariantLabel(), state)
            }
        } else {
            null
        }
    }

    if (variants.isEmpty()) {
        return if (includeImpliedDefault) listOf(impliedDefaultAccessControlVariant()) else emptyList()
    }

    return variants.sortedWith(compareBy({ if (it.key == "default") 0 else 1 }, { it.label }))
}

fun JsonObject.toAccessControlState(): AccessControlState? {
    val visible = this["visible"].asBooleanOrNull() ?: true
    val discoverable = this["discoverable"].asBooleanOrNull() ?: true
    val recommendable = this["recommendable"].asBooleanOrNull() ?: true
    return AccessControlState(visible, discoverable, recommendable)
}

private fun String.toVariantLabel(): String = when (this) {
    "default" -> "Default"
    "auto360l" -> "Auto 360L"
    else -> splitIntoWords().ifEmpty { listOf(this) }.joinToString(" ")
}

private fun String.splitIntoWords(): List<String> {
    val splitRegex = Regex("(?<=\\D)(?=\\d)|(?<=\\d)(?=\\D)|(?<=\\p{Lower})(?=\\p{Upper})|[_-]")
    val tokens = this.split(splitRegex).filter { it.isNotBlank() }
    if (tokens.isEmpty()) return emptyList()

    val normalized = mutableListOf<String>()
    tokens.forEach { token ->
        when {
            token.length == 1 && token[0].isLetter() && normalized.lastOrNull()?.all { it.isDigit() } == true ->
                normalized[normalized.lastIndex] = normalized.last() + token.uppercase()
            token.all { it.isDigit() } -> normalized += token
            token.all { it.isUpperCase() || it.isDigit() } -> normalized += token.uppercase()
            else -> normalized += token.replaceFirstChar { if (it.isLowerCase()) it.uppercaseChar() else it }
        }
    }
    return normalized
}

private fun JsonElement?.asBooleanOrNull(): Boolean? = when (this) {
    null -> null
    is JsonPrimitive -> when {
        this.booleanOrNull != null -> this.booleanOrNull
        this.intOrNull != null -> this.intOrNull == 1
        this.isString -> when (this.content.lowercase()) {
            "true", "1", "yes" -> true
            "false", "0", "no" -> false
            else -> null
        }
        else -> null
    }
    else -> null
}
